import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = anonymous, object = authenticated
  const [user, setUser] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);

  const persist = (token, u) => {
    localStorage.setItem("ak_token", token);
    setUser(u);
  };

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("ak_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("ak_token");
      setUser(false);
    }
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persist(data.token, data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    persist(data.token, data.user);
    return data.user;
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("ak_token");
    setUser(false);
  };

  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      if (sid) {
        (async () => {
          try {
            const { data } = await api.post("/auth/google", { session_id: sid });
            persist(data.token, data.user);
          } catch {
            setUser(false);
          }
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
        })();
        return;
      }
    }
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider
      value={{ user, setUser, login, register, googleLogin, logout, checkAuth, authOpen, setAuthOpen }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
